import { Box, Button, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';

/**
 * Sticky toolbar shown above a list when `count > 0`. Renders the action
 * buttons supplied by the caller; each action receives the click event so it
 * can read the current `selectedIds` from its closure.
 *
 * Usage:
 *   <BulkActionsBar
 *     count={selection.count}
 *     onClear={selection.clear}
 *     actions={[
 *       { label: 'Delete', icon: <DeleteIcon />, onClick: () => deleteMany(selection.selectedIds), color: 'error' },
 *     ]}
 *   />
 */
const BulkActionsBar = ({ count, onClear, actions }) => {
  if (!count) return null;
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        mb: 2,
        px: 2,
        py: 1.25,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          size="small"
          variant="text"
          startIcon={<CloseIcon />}
          onClick={onClear}
          sx={{ color: 'inherit' }}
          aria-label="Clear selection"
        >
          Clear
        </Button>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {count} selected
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        {actions.map((a, i) => (
          <Button
            key={a.key || `bulk-${i}`}
            size="small"
            variant="contained"
            color={a.color === 'error' ? 'error' : 'secondary'}
            startIcon={a.icon}
            onClick={a.onClick}
            disabled={a.disabled}
          >
            {a.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

BulkActionsBar.propTypes = {
  count: PropTypes.number.isRequired,
  onClear: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      onClick: PropTypes.func.isRequired,
      color: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ).isRequired,
};

export default BulkActionsBar;
